'use client';

import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { CARD_SCHEME_PROFILES, getCardSchemeForNumber } from '../steps/CardDetailsStep/cardSchemes';

type AmountStepValues = {
  amount: string;
  termsAccepted: boolean;
  paymentMethod: string;
};

type MethodStepValues = {
  cardNumber?: string;
  expiryDate?: string;
  cvv?: string;
  cardholderName?: string;
};

type DepositFormValues = {
  amountStep: AmountStepValues;
  methodStep: MethodStepValues;
};

function useDepositSheet() {
  const t = useTranslations('global_sheets.deposit');
  const {
    PAYMENTS_FLAGS: { acceptsAmexCards },
    DEPOSIT_AMOUNTS: { defaultAmount },
  } = getSiteConfig();

  /**
   * Function to calculate the check sum of a card number
   * @param cardNumber - The card number to calculate the check sum for
   * @returns The check sum of the card number
   */
  const getCheckSum = useCallback((cardNumber: string) => {
    let checkSum = 0,
      gw = 1;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
      if (text2Int(i, cardNumber) !== -1) {
        checkSum += crossSum(text2Int(i, cardNumber) * gw);
        gw = 3 - gw;
      }
    }
    return checkSum;

    function crossSum(x: number) {
      return (x % 10) + Math.floor(x / 10);
    }

    function text2Int(index: number, text: string) {
      if (text.charAt(index) < '0' || text.charAt(index) > '9') {
        return -1;
      }
      return parseInt(text.slice(index, index + 1), 10);
    }
  }, []);

  /**
   * Function to validate the check sum of a card number. Invalid checksum means invalid card number.
   * @param checkSum - The check sum of the card number, obtained from getCheckSum function
   * @param cardNumber - The card number to validate
   * @returns True if the check sum is valid, false otherwise
   */
  const isValidCheckSum = useCallback((checkSum: number, cardNumber: string) => {
    return checkSum % 10 === 0 && cardNumber !== '' && cardNumber.length > 10;
  }, []);

  const amountSchema = useMemo(
    () => ({
      amountStep: z.object({
        amount: z
          .string()
          .min(1, t('amount.required_error'))
          .refine(val => !isNaN(Number(val)) && Number(val) > 0, t('amount.invalid_error')),
        termsAccepted: z.boolean().refine(val => val === true, t('terms.required_error')),
        paymentMethod: z.string().min(1, t('payment_method.required_error')),
      }),
    }),
    [t]
  );

  // Function to generate dynamic schema based on payment method selected in first step
  const getMethodSchema = useCallback(
    (paymentMethod: string) => {
      switch (paymentMethod) {
        case 'Debit card':
          return {
            methodStep: z
              .object({
                cardNumber: z
                  .string()
                  .min(1, t('card_details.card_number.required_error'))
                  .refine(value => isValidCheckSum(getCheckSum(value), value), t('card_details.card_number.invalid_error')),
                expiryDate: z
                  .string()
                  .min(1, t('card_details.expiry_date.required_error'))
                  .min(5, t('card_details.expiry_date.invalid_error'))
                  .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, t('card_details.expiry_date.invalid_error'))
                  .refine(value => {
                    const [month, year] = value.split('/').map(Number);
                    const currentDate = new Date();
                    const currentYear = currentDate.getFullYear() % 100;
                    const currentMonth = currentDate.getMonth() + 1;

                    return year && month && (year > currentYear || (year === currentYear && month >= currentMonth));
                  }, t('card_details.expiry_date.invalid_error')),
                cvv: z.string().min(1, t('card_details.cvv.required_error')),
                cardholderName: z.string().min(1, t('card_details.cardholder_name.required_error')).min(2, t('card_details.cardholder_name.invalid_error')),
              })
              // Length/CVV rules follow the card scheme detected from the number. AMEX (15 digits, 4-digit CVV)
              // is only recognised where the market accepts it; elsewhere a 15-digit number fails the 16-digit rule.
              .superRefine((data, ctx) => {
                const cardDigits = (data.cardNumber ?? '').replace(/\D/g, '');
                const profile = CARD_SCHEME_PROFILES[getCardSchemeForNumber(cardDigits, acceptsAmexCards)];
                if (cardDigits.length !== profile.cardDigits) {
                  ctx.addIssue({
                    code: 'custom',
                    path: ['cardNumber'],
                    message: t('card_details.card_number.invalid_error'),
                  });
                }
                if ((data.cvv ?? '').length !== profile.cvvDigits) {
                  ctx.addIssue({
                    code: 'custom',
                    path: ['cvv'],
                    message: t('card_details.cvv.invalid_error'),
                  });
                }
              }),
          };
        default:
          return {
            methodStep: z.object({
              cardNumber: z.string().optional(),
              expiryDate: z.string().optional(),
              cvv: z.string().optional(),
              cardholderName: z.string().optional(),
            }),
          };
      }
    },
    [t, getCheckSum, isValidCheckSum, acceptsAmexCards]
  );

  const getAmountStepInitialValues = useCallback(
    (initialAmount?: string) => {
      return {
        amount: initialAmount || String(defaultAmount / 100),
        termsAccepted: false,
        paymentMethod: '',
      };
    },
    [defaultAmount]
  );

  const getMethodStepInitialValues = useCallback((paymentMethod: string, cardholderName = ''): MethodStepValues => {
    switch (paymentMethod) {
      case 'Debit card':
        return {
          cardNumber: '',
          expiryDate: '',
          cvv: '',
          cardholderName,
        };
      default:
        return {};
    }
  }, []);

  const createFormSchema = useCallback(
    (currentPaymentMethod: string) => {
      return z.object({
        ...amountSchema,
        ...getMethodSchema(currentPaymentMethod),
      });
    },
    [amountSchema, getMethodSchema]
  );

  return {
    amountSchema,
    getAmountStepInitialValues,
    getMethodSchema,
    getMethodStepInitialValues,
    createFormSchema,
  };
}

export { useDepositSheet, type AmountStepValues, type DepositFormValues, type MethodStepValues };
